import {useEffect, useRef, useState} from 'react'
import { activeDiscount, trendDiscount, type TrendPipePricing } from '../utils/discount'
import type {GenerationCardSettings} from '../types/generationCard'

export interface PopularVideoItem {
	/** id айтема (атрибуция source_card_id и «Своя цена»; у legacy нет). */
	id?: string
	url: string
	title?: string
	description?: string
	target_adapter?: string
	target_model?: string
	system_prompt?: string
	// Пресет параметров генерации (resolution/quality/duration/seedanceMode…),
	// улетает в виджет /generate при клике (см. HomeTemp onVideoClick).
	settings?: GenerationCardSettings
	/** Пайплайн-карточка: slug шаблона (тренды «фото → видео» в GTM). */
	pipeline_slug?: string
	/** Этапы тренда: цены/акция для витринной плашки (движок — шаблон бэка). */
	pipeline?: TrendPipePricing
	/** Медиа-референсы «под капотом» + локи полей юзера (см. админку витрины). */
	reference_video_url?: string
	user_video_locked?: boolean
	reference_audio_url?: string
	user_audio_locked?: boolean
	/** Слот-колода (0 большая, 1 правая верхняя, 2 правая нижняя). Задаётся
	 *  админкой; у старых записей нет — фолбэк на раскладку по кругу (i % 3). */
	slot?: number
	/** «Своя цена» + акция (скидочная цена и срок ISO UTC) — как у карточек. */
	price_override?: number
	discount_price?: number
	discount_until?: string
}

interface Props {
	title?: string
	videos: PopularVideoItem[]
	onVideoClick?: (video: PopularVideoItem) => void
}

// В слайдере живут И видео, И фото: тип определяем по расширению url.
// Фото рендерится <img> и авто-листается ТАЙМЕРОМ (у него нет own-триггера
// «доиграло», как onEnded у видео).
const IMAGE_URL_RE = /\.(jpe?g|png|webp|gif|avif)(\?|$)/i
const isImageUrl = (u: string) => IMAGE_URL_RE.test(u)

// Сколько фото висит на верху колоды до авто-перелистывания
const PHOTO_AUTO_ADVANCE_MS = 5000

// Раскладка по колодам: слот i получает видео i, i+3, i+6… — первые три
// ролика остаются на привычных местах (большой + два малых), остальные
// ложатся ПОЗАДИ них стопками «как колода карт». Каждая колода листается
// независимо: клик по краешку задней карты / свайп / авто-переход по
// окончании ролика (для фото — по таймеру).
function buildDecks(videos: PopularVideoItem[]): PopularVideoItem[][] {
	const decks: PopularVideoItem[][] = [[], [], []]
	videos.forEach((v, i) => {
		const slot = typeof v.slot === 'number' && v.slot >= 0 && v.slot <= 2 ? v.slot : i % 3
		decks[slot].push(v)
	})
	return decks.filter((d) => d.length > 0)
}

function VideoDeck({deck, index, onVideoClick}: {deck: PopularVideoItem[]; index: number; onVideoClick?: (video: PopularVideoItem) => void}) {
	const videoRef = useRef<HTMLVideoElement>(null)
	const containerRef = useRef<HTMLDivElement>(null)
	const touchX = useRef<number | null>(null)
	const hovered = useRef(false)
	const [isVisible, setIsVisible] = useState(false)
	const [top, setTop] = useState(0)
	// Уходящая карта живёт ghost-слоем ПОВЕРХ новой и гаснет (mode задаёт
	// темп: manual — быстрый полёт, auto — медленный кроссфейд). Новая карта
	// под ним сразу непрозрачна — задние краешки не просвечивают в момент
	// смены (прежняя схема «прозрачный вход» показывала сквозь новую карту
	// уже пересчитанный задник → визуальный отскок 1→2→1→2).
	const [ghost, setGhost] = useState<{ url: string; isImage: boolean; time: number; mode: 'manual' | 'auto' } | null>(null)
	// Счётчик листаний: key ремоунтит верхнюю карту → проигрывается анимация
	// «выезда из стопки» (заметный эффект замены). 0 = первый маунт, без
	// анимации — колода не дёргается при загрузке страницы.
	const [swaps, setSwaps] = useState(0)
	// Актуальный top для отложенных таймеров (пауза перед авто-сменой):
	// если юзер успел листнуть сам — отложенное авто-листание отменяется.
	const topRef = useRef(top)
	useEffect(() => { topRef.current = top }, [top])
	const ghostRef = useRef<typeof ghost>(null)
	useEffect(() => { ghostRef.current = ghost }, [ghost])

	const len = deck.length
	const topVideo = deck[top % len]
	const topIsImage = isImageUrl(topVideo.url)
	// Позади верхней — до двух следующих карт (только их краешки и видны)
	const backs = len > 1 ? [deck[(top + 1) % len], len > 2 ? deck[(top + 2) % len] : null].filter(Boolean) as PopularVideoItem[] : []

	useEffect(() => {
		const container = containerRef.current
		if (!container) return

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setIsVisible(true)
					observer.disconnect()
				}
			},
			{threshold: 0.1, rootMargin: '200px'}
		)
		observer.observe(container)
		return () => observer.disconnect()
	}, [])

	useEffect(() => {
		if (isVisible && videoRef.current) {
			videoRef.current.play().catch(() => {})
		}
	}, [isVisible, top])

	// Листание: текущая карта СРАЗУ снимается ghost-снимком поверх, top
	// меняется мгновенно (новая карта под ghost'ом уже непрозрачна), ghost
	// гаснет. Guard от дабл-клика — пока ghost жив, не листаем.
	const advance = (dir: 1 | -1 = 1, mode: 'manual' | 'auto' = 'manual') => {
		if (len < 2 || ghost) return
		const cur = deck[top % len]
		const curIsImage = isImageUrl(cur.url)
		// Для видео запоминаем текущий кадр — ghost доиграет с того же места
		// (по ended это последний кадр: карта «застывает» и растворяется)
		const time = !curIsImage && videoRef.current ? videoRef.current.currentTime || 0 : 0
		setGhost({ url: cur.url, isImage: curIsImage, time, mode })
		setTop((t) => (t + dir + len) % len)
		setSwaps((s) => s + 1)
		window.setTimeout(() => setGhost(null), mode === 'auto' ? 720 : 1050)
	}

	// Авто-листание ФОТО: у картинки нет onEnded — держим 5 секунд и листаем
	// тем же деликатным авто-кроссфейдом. Курсор на карте — не дёргаем
	// (перепроверка каждые 5с, как hover-петля у видео). Таймер чистится при
	// смене карты (в т.ч. ручном листании) и уходе из вьюпорта.
	const advanceRef = useRef(advance)
	useEffect(() => {
		advanceRef.current = advance
	})

	// Отложенное авто-листание (пауза после конца ролика), которое НЕ теряется:
	// если ghost предыдущей смены ещё жив (короткий ролик кончился раньше,
	// чем догорела анимация) — ждём и пробуем снова, а не молча выходим.
	// Без этого колода замирала навсегда: гвард advance съедал вызов,
	// а новых триггеров у доигравшего видео нет.
	const scheduleAutoAdvance = (fromTop: number, delay: number) => {
		window.setTimeout(() => {
			if (topRef.current !== fromTop) return // юзер уже листнул сам
			if (ghostRef.current) {
				scheduleAutoAdvance(fromTop, 250)
				return
			}
			advanceRef.current(1, 'auto')
		}, delay)
	}
	useEffect(() => {
		if (!isVisible || len < 2 || !topIsImage) return
		let t = 0
		const arm = () => {
			t = window.setTimeout(() => {
				if (hovered.current) arm()
				else advanceRef.current(1, 'auto')
			}, PHOTO_AUTO_ADVANCE_MS)
		}
		arm()
		return () => window.clearTimeout(t)
	}, [top, isVisible, len, topIsImage])

	return (
		<div
			ref={containerRef}
			className={`video-showcase-card video-showcase-card--${index + 1}`}
			onMouseEnter={() => { hovered.current = true }}
			onMouseLeave={() => { hovered.current = false }}
			onTouchStart={(e) => { touchX.current = e.touches[0]?.clientX ?? null }}
			onTouchEnd={(e) => {
				if (touchX.current == null) return
				const dx = (e.changedTouches[0]?.clientX ?? touchX.current) - touchX.current
				touchX.current = null
				// Свайп влево — следующая карта, вправо — предыдущая
				if (Math.abs(dx) > 44) advance(dx < 0 ? 1 : -1)
			}}
		>
			<div className={`video-deck${len > 1 ? ' video-deck--stacked' : ''}`}>
				{backs.map((v, k) => (
					<button
						key={`${v.url}-${k}`}
						type="button"
						className={`video-deck__back video-deck__back--${k + 1}`}
						onClick={(e) => { e.stopPropagation(); advance() }}
						aria-label="Следующее видео"
						title="Следующее видео"
						tabIndex={k === 0 ? 0 : -1}
					>
						{/* Статичный первый кадр — играет ТОЛЬКО верхняя карта */}
						{isImageUrl(v.url) ? (
							<img src={isVisible ? v.url : undefined} alt="" loading="lazy" decoding="async" aria-hidden="true" />
						) : (
							<video src={isVisible ? v.url : undefined} muted playsInline preload="metadata" aria-hidden="true" />
						)}
					</button>
				))}

				<div
					key={`top-${top}-${swaps}`}
					className={`video-showcase-item video-deck__top${swaps > 0 ? ' video-deck__top--swap' : ''}${topVideo.target_adapter ? ' video-showcase-card--clickable' : ''}`}
					onClick={() => {
						if (topVideo.target_adapter) onVideoClick?.(topVideo)
					}}
					style={topVideo.target_adapter ? {cursor: 'pointer'} : undefined}
				>
					{topIsImage ? (
						<img
							key={`${topVideo.url}-${top}`}
							className="video-showcase-video"
							src={isVisible ? topVideo.url : undefined}
							alt={topVideo.title || ''}
							loading="lazy"
							decoding="async"
						/>
					) : (
					<video
						ref={videoRef}
						key={`${topVideo.url}-${top}`}
						className="video-showcase-video"
						src={isVisible ? topVideo.url : undefined}
						muted
						playsInline
						// metadata вместо auto: auto тянул ВСЕ файлы целиком при появлении
						// (3 видео параллельно). play() докачивает поток по мере проигрывания.
						preload='metadata'
						onEnded={(e) => {
							// Стопка: доиграло → ПАУЗА, затем медленный кроссфейд к
							// следующей карте. Если юзер сейчас смотрит (курсор на
							// карте) — не дёргаем, тихо заводим ролик по кругу.
							if (len > 1 && !hovered.current) {
								scheduleAutoAdvance(topRef.current, 450)
								return
							}
							const video = e.currentTarget
							video.classList.add('is-resetting')
							setTimeout(() => {
								video.currentTime = 0
								void video.play().catch(() => {})
							}, 140)
							setTimeout(() => {
								video.classList.remove('is-resetting')
							}, 220)
						}}
					/>
					)}
					{/* Акция айтема: «−N% · срок» (правый верх — как у карточек).
					    Считается на рендере; цена в GTM и списание совпадут (пин
					    несёт эффективную, бэк клампит по своей UTC-проверке). */}
					{(() => {
						// Тренд-слайд: акция живёт в этапах (−N% на обе цены) — плашка
						// из суммы промо-цен, формула та же, по которой спишет бэк.
						const info = topVideo.pipeline?.enabled
							? trendDiscount(topVideo.pipeline)
							: activeDiscount(topVideo.price_override, topVideo.discount_price, topVideo.discount_until)
						if (!info) return null
						return (
							<span className="video-deck__discount" aria-label={`Скидка ${info.percent}%`}>
								−{info.percent}% · {info.leftLabel}
							</span>
						)
					})()}
					{len > 1 && (
						<div className="video-deck__nav" onClick={(e) => e.stopPropagation()}>
							<button type="button" className="video-deck__nav-btn" onClick={() => advance(-1)} aria-label="Предыдущее видео" title="Предыдущее">
								<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
									<path d="M15 5l-7 7 7 7" />
								</svg>
							</button>
							<span className="video-deck__count" aria-label={`Видео ${(top % len) + 1} из ${len}`}>
								{(top % len) + 1}/{len}
							</span>
							<button type="button" className="video-deck__nav-btn" onClick={() => advance(1)} aria-label="Следующее видео" title="Следующее">
								<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
									<path d="M9 5l7 7-7 7" />
								</svg>
							</button>
						</div>
					)}
				</div>

				{/* Ghost уходящей карты: лежит ПОВЕРХ новой и гаснет — под ним
				    уже стоит следующая карта в полной непрозрачности */}
				{ghost && (
					<div className={`video-showcase-item video-deck__ghost video-deck__ghost--${ghost.mode}`} aria-hidden="true">
						{ghost.isImage ? (
							<img src={ghost.url} alt="" />
						) : (
							<video
								src={ghost.url}
								muted
								playsInline
								preload="metadata"
								onLoadedMetadata={(e) => {
									// Восстанавливаем кадр, на котором карту сняли
									try { e.currentTarget.currentTime = ghost.time } catch { /* noop */ }
								}}
							/>
						)}
					</div>
				)}
			</div>

			{/* Подпись — от верхней карты; key перезапускает fade при листании */}
			<div className="video-showcase-meta" key={top}>
				<div className="video-showcase-title">
					{topVideo.title?.trim() || 'Оживи фото — заставь его двигаться'}
				</div>
				<div className="video-showcase-description">
					{topVideo.description?.trim() || 'Изображения превращаются в живые портреты'}
				</div>
			</div>
		</div>
	)
}

export default function PopularVideoShowcase({title, videos, onVideoClick}: Props) {
	if (!videos.length) return null

	const decks = buildDecks(videos)

	return (
		<section className="popular-video-wrapper">
			{title && <h2 className="h2">{title}</h2>}
			<div className="video-showcase-grid">
				{decks.map((deck, index) => (
					<VideoDeck
						key={`deck-${index}-${deck[0]?.url ?? ''}`}
						deck={deck}
						index={index}
						onVideoClick={onVideoClick}
					/>
				))}
			</div>
		</section>
	)
}
